<template>
  <v-stepper
    v-model="internalStep"
    alt-labels
    flat
    class="stepper ns-stepper"
    :width="width"
  >
    <v-stepper-header class="ns-stepper__header" plain>
      <!-- Render steps and connectors using computed property approach -->
      <template v-for="item in stepItems">
        <!-- STEP -->
        <v-stepper-step
          v-if="item.type === 'step'"
          :key="item.key + '-step'"
          :step="item.stepNumber"
          :complete="internalStep > item.stepNumber"
          :editable="editable"
          :color="stepColor(item.stepNumber)"
          class="custom-step"
          @click="goTo(item.stepNumber)"
        >
          <template #default>
            <div class="step-chip pa-2" :class="stepClass(item.stepNumber)">
              <!-- broj (lijevi krug) -->
              <span class="step-icon"><span>{{ item.stepNumber }}</span></span>

              <span class="step-label">{{ item.step.title }}</span>
              <!-- status ikona (fiksna širina da čip ne mijenja dimenziju) -->
              <span class="status-slot ml-2">
                <template v-if="internalStep > item.stepNumber">
                  <template v-if="usePathIcon">
                    <svg class="icon-plain" viewBox="0 0 24 24">
                      <path :d="icons.mdiCheck" />
                    </svg>
                  </template>
                  <v-icon v-else class="icon-plain">{{ icons.mdiCheck }}</v-icon>
                </template>
                <template v-else-if="internalStep === item.stepNumber">
                  <template v-if="usePathIcon">
                    <svg class="icon-plain" viewBox="0 0 24 24">
                      <path :d="icons.mdiFlag" />
                    </svg>
                  </template>
                  <v-icon v-else class="icon-plain">{{ icons.mdiFlag }}</v-icon>
                </template>
              </span>
            </div>
          </template>
        </v-stepper-step>

        <!-- CONNECTOR / DIVIDER -->
        <div
          v-else-if="item.type === 'connector'"
          :key="item.key + '-connector'"
          class="connector"
          :class="connectorClass(item.leftStepNumber)"
        ></div>
      </template>
    </v-stepper-header>

    <v-stepper-items>
      <v-stepper-content
        v-for="(step, contentIndex) in steps"
        :key="'ci'+contentIndex"
        :step="contentIndex + 1"
        class="pl-0 ml-0"
      >
        <slot name="step" :index="contentIndex" :step="contentIndex + 1" />
      </v-stepper-content>
    </v-stepper-items>
  </v-stepper>
</template>

<script>
import { mdiCheck, mdiFlag } from '@mdi/js';

export default {
  name: 'ReusableStepper',
  // Vue 2 v-model
  model: { prop: 'value', event: 'input' },
  props: {
    value: { type: Number, default: 1 },
    steps: { type: Array, default: () => [] }, // [{ title }]
    width: { type: [String, Number], default: 950 },
    editable: { type: Boolean, default: true },
    usePathIcon: { type: Boolean, default: false },
    icons: { type: Object, default: () => ({ mdiCheck, mdiFlag }) },
  },
  data() {
    return { internalStep: this.value || 1 };
  },
  computed: {
    stepItems() {
      const items = [];
      this.steps.forEach((step, index) => {
        // Add step
        items.push({
          type: 'step',
          key: `s${index}`,
          stepNumber: index + 1,
          step: step
        });
        
        // Add connector after step (except for last step)
        if (index < this.steps.length - 1) {
          items.push({
            type: 'connector',
            key: `c${index}`,
            leftStepNumber: index + 1
          });
        }
      });
      return items;
    }
  },
  watch: {
    value(v) { if (v !== this.internalStep) this.internalStep = v || 1; },
    internalStep(v) { this.$emit('input', v); },
  },
  methods: {
    goTo(n) { if (this.editable) this.internalStep = n; },
    stepColor(n) {
      if (this.internalStep > n) return 'success';
      if (this.internalStep === n) return 'primary';
      return 'grey lighten-1';
    },
    stepClass(n) {
      if (this.internalStep > n) return 'step--success';
      if (this.internalStep === n) return 'step--active';
      return 'step--idle';
    },
    connectorClass(leftIndex) {
      if (this.internalStep > leftIndex) return 'connector--success';
      if (this.internalStep === leftIndex) return 'connector--active';
      return 'connector--idle';
    },
  },
};
</script>

<style scoped>
/* ===== Size controls (copy from your old component) ===== */
.stepper {
  --chip-font-size: 11px;   /* text size in pill */
  --chip-pad-y: 3px;        /* vertical padding */
  --chip-pad-x: 10px;       /* horizontal padding */
  --chip-icon-d: 15px;      /* LEFT number circle diameter */
  --status-icon-size: 14px; /* RIGHT plain flag/check size */
}

/* shrink v-icon and svg variants */
.stepper :deep(.icon-plain),
.stepper :deep(.icon-plain.v-icon) {
  font-size: var(--status-icon-size) !important;  /* v-icon font mode */
  width: var(--status-icon-size) !important;
  height: var(--status-icon-size) !important;
  line-height: var(--status-icon-size) !important;
  margin: 0 !important;
}
.stepper :deep(.icon-plain .v-icon__svg),
.stepper :deep(.icon-plain svg) {
  width: 100% !important;   /* mdiSvg / inline <svg> */
  height: 100% !important;
}

/* keep the whole chip on one line */
.step-chip { white-space: nowrap; }
.step-label { white-space: nowrap; }

/* keep container clean & kill bg */
.stepper,
.stepper :deep(.v-stepper__header) {
  background: transparent !important;
  box-shadow: none !important;
}

/* Hide Vuetify’s default outer circle */
.stepper :deep(.custom-step .v-stepper__step__step) {
  display: none !important;
}



/* neutralize the built-in label; we style our own chip */
.stepper :deep(.custom-step .v-stepper__label) {
  background: transparent !important;
  padding: 0 !important;
  gap: 0 !important;
  box-shadow: none !important;
  color: inherit !important;
}
.stepper :deep(.custom-step .v-ripple__container) { display: none !important; }

/* ===== CONNECTORS (dash linija, bez “otoka”) ===== */
.connector {
  align-self: center;
  height: 0;
  border-top: 2px dashed rgba(0,0,0,.25);
  margin: 0;                /* NEMA bočnih margina -> spaja se uz step */
  width: 185px; 
  flex: none;
  gap: 0px;
  opacity: .9;
}
.theme--dark .connector { border-top-color: rgba(255,255,255,.28); }
.connector--success { border-top-color: #43a047; }
.connector--active  { border-top-style: solid; border-top-color: #1976d2; }

/* ===== PILL CHIP (isti vizual) ===== */
.step-chip {
  display: inline-flex;
  align-items: center;
  padding: var(--chip-pad-y) var(--chip-pad-x);
  border-radius: 9999px;
  font-weight: 500;
  font-size: var(--chip-font-size);
  gap: 8px;
  transition: background-color .2s ease, color .2s ease;
}

/* lijevi broj (krug) */
.step-icon {
  width: var(--chip-icon-d);
  height: var(--chip-icon-d);
  min-width: var(--chip-icon-d);
  border-radius: 50%;
  font-size: calc(var(--chip-font-size) - 1px);
  display: flex; align-items: center; justify-content: center;
}

/* plain status icons (no background) */
.icon-plain {
  display: inline-flex;
  align-items: center;
  color: inherit;
  line-height: 1;
  font-size: var(--status-icon-size) !important;  /* for <v-icon> */
}
.icon-plain svg {
  width: var(--status-icon-size);
  height: var(--status-icon-size);
}

/* ===== STATES (isti kao u starom) ===== */
.step--idle { background: #6b72801c; color: #9aa1a9; padding-left: 4px; }
.step--idle .step-icon { background: #9aa1a91a; color: #9aa1a9; border-color: #9aa1a940; }

.step--active { background: #1976d2; color: #fff; padding-left: 4px; }
.step--active .step-icon { background: #fff; color: #1976d2; border-color: #1976d2; }

.step--success { background: #2e7d32; color: #fff; padding-left: 4px; }
.step--success .step-icon { background: #fff; color: #2e7d32; border-color: '#2e7d2'; }

/* Dark theme tweaks (isti kao prije) */
.theme--dark .step--idle { background: #ffffff14; color: #b8c0cc; }
.theme--dark .step--idle .step-icon { background: #ffffff12; color: #b8c0cc; border-color: #ffffff33; }

/* Nuke ALL hover/focus visual changes inside the stepper */
.stepper :deep(.v-stepper__step),
.stepper :deep(.v-stepper__label),
.stepper :deep(.v-stepper__step:hover),
.stepper :deep(.v-stepper__step:focus),
.stepper :deep(.v-stepper__label:hover),
.stepper :deep(.v-stepper__label:focus) {
  box-shadow: none !important;
  background: transparent !important;
  filter: none !important;
  transform: none !important;
}

/* Remove Vuetify's hover overlay layer */
.stepper :deep(.v-stepper__step::before),
.stepper :deep(.v-stepper__label::before) {
  content: none !important;
}

/* Kill ripple just in case */
.stepper :deep(.v-ripple__container) {
  display: none !important;
}

/* Flatten any elevation */
.stepper :deep([class*="elevation-"]) {
  box-shadow: none !important;
}

/* Kill ALL hover effects on the chip text and its children */
.stepper :deep(.step-chip),
.stepper :deep(.step-chip *),
.stepper :deep(.step-label),
.stepper :deep(.step-label *) {
  box-shadow: none !important;
  text-shadow: none !important;
  filter: none !important;
  transform: none !important;
  transition: none !important;
  text-decoration: none !important;
}

/* and make sure hover doesn't re-introduce anything */
.stepper :deep(.step-chip:hover),
.stepper :deep(.step-chip *:hover),
.stepper :deep(.step-label:hover),
.stepper :deep(.step-label *:hover) {
  box-shadow: none !important;
  text-shadow: none !important;
  filter: none !important;
  transform: none !important;
}

/* (optional) remove mobile tap highlight on the chip */
.stepper :deep(.step-chip) { -webkit-tap-highlight-color: transparent; }

/* ===== Header cleanup (bez paddinga koji gura prvi chip) ===== */
.ns-stepper__header {
  display: flex;
  align-items: center;
  gap: 0;
  padding: 0 !important;
  margin: 0 !important;
}
.stepper :deep(.v-stepper__header .v-stepper__step),
.stepper :deep(.v-stepper__header .v-stepper__label) {
  padding: 0 !important;
  margin: 0 !important;
}

</style>
